/**
 * Helpers de typage et d'URL pour la pièce jointe d'un informe (image / pdf / html).
 *
 * Pendant de `documentTypes.js` côté documents joueurs, à ceci près qu'un informe
 * ne porte qu'UN seul média, identifié par son nom de fichier (`media_filename`)
 * et non par un mimetype stocké en base — d'où un typage par extension.
 *
 * Partagés entre CreateInforme, InformeTab, RechercheInforme et l'onglet Instat,
 * où ces helpers étaient auparavant dupliqués à l'identique.
 */

import { getMediaBaseUrl } from './mediaUrl';

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

const extOf = (filename) => (filename ? String(filename).split('.').pop()?.toLowerCase() : '');

/** URL statique du média d'un informe (même chemin que les avatars). */
export const getInformeMediaUrl = (mediaFilename) =>
  `${getMediaBaseUrl()}/mediarapport/${mediaFilename}?${new Date().getTime()}`;

/**
 * URL de la vignette d'aperçu générée au backend pour les pièces jointes HTML.
 * Absente si la génération a échoué → le front retombe sur une icône typée.
 */
export const getInformeThumbUrl = (informeId) =>
  `${getMediaBaseUrl()}/mediarapport/${informeId}_thumb.webp`;

export const isImageMedia = (filename) => IMAGE_EXTS.includes(extOf(filename));
export const isPdfMedia = (filename) => extOf(filename) === 'pdf';
export const isHtmlMedia = (filename) => ['html', 'htm'].includes(extOf(filename));

// Taille max d'une pièce jointe d'informe — alignée sur le backend.
export const MAX_MEDIA_SIZE = 10 * 1024 * 1024;

// Types MIME acceptés à l'upload
export const VALID_MEDIA_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf', 'text/html',
];

// Extensions acceptées en repli quand le navigateur n'envoie pas de mimetype fiable
export const VALID_MEDIA_EXTS = ['.html', '.htm'];

// Valeur de l'attribut `accept` des <input type="file"> — source unique
export const MEDIA_ACCEPT_ATTR =
  'image/jpeg,image/png,image/gif,image/webp,application/pdf,text/html,.html,.htm';

// Libellé des formats acceptés, affiché sous les zones de dépôt
export const MEDIA_FORMATS_LABEL = 'JPG, PNG, GIF, WebP, PDF, HTML • Max 10MB';

/**
 * Valide un objet File côté navigateur (type MIME ou, à défaut, extension).
 * @returns {boolean}
 */
export const isAcceptedMedia = (file) => {
  if (!file) return false;
  const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
  return VALID_MEDIA_TYPES.includes(file.type) || VALID_MEDIA_EXTS.includes(ext);
};

/** Vrai si l'objet File sélectionné est un HTML (mimetype absent toléré). */
export const isHtmlFile = (file) =>
  !!file && (file.type === 'text/html' || /\.html?$/i.test(file.name));

/**
 * Forme l'objet attendu par `DocumentMediaDialog` à partir d'un informe, pour
 * réutiliser tel quel l'aperçu HTML des documents (iframe sandbox).
 */
export const asMediaDoc = (informe) => ({
  file_type: 'text/html',
  original_name: informe.media_filename,
  titre: null,
});
